import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import { Circle as CircleStyle, Fill, Icon, RegularShape, Stroke, Style, Text } from 'ol/style.js';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import 'ol/ol.css';
import * as proj from 'ol/proj';
import {
    DEFAULT_GROUP_ANNOTATION_STYLE,
    DEFAULT_USER_ANNOTATION_STYLE,
    normalizeAnnotationStyle
} from './annotationStyles.js';
import { getAnnotationIcon } from './annotationIcons.js';

const ANNOTATION_HIT_TOLERANCE_PX = 8;

var vectorAnnotationsByUser;
var vectorAnnotationsByGroup;
var annotationStyleCache = {};

function deduplicateAnnotations(annotations) {
    var uniqueAnnotations = new Map();
    (Array.isArray(annotations) ? annotations : []).forEach(function (annotation) {
        if (annotation && annotation.id) uniqueAnnotations.set(annotation.id, annotation);
    });
    return Array.from(uniqueAnnotations.values());
}

function wrapAnnotationLabel(text) {
    var words = String(text || '').trim().split(/\s+/);
    var lines = [];
    var currentLine = '';

    words.forEach(function (word) {
        var nextLine = currentLine ? currentLine + ' ' + word : word;
        if (nextLine.length <= 34 || !currentLine) {
            currentLine = nextLine;
            return;
        }
        lines.push(currentLine);
        currentLine = word;
    });

    if (currentLine) lines.push(currentLine);
    return lines.join('\n');
}

// Exportada para poder probar el contrato de la etiqueta (QA-213) sin montar
// el mapa.
export function getAnnotationStyle(feature, fallbackStyle) {
    var annotationStyle = normalizeAnnotationStyle(feature.get('annotationStyle'), fallbackStyle);
    var labelText = annotationStyle.show_label ? String(feature.get('text') || '').trim() : '';
    var cacheKey = [
        annotationStyle.symbol,
        annotationStyle.color,
        annotationStyle.size,
        annotationStyle.show_label,
        labelText
    ].join(':');

    if (annotationStyleCache[cacheKey]) return annotationStyleCache[cacheKey];

    var fill = new Fill({ color: annotationStyle.color });
    var stroke = new Stroke({ color: '#ffffff', width: 2 });
    var image;
    var text;
    var iconDefinition = getAnnotationIcon(annotationStyle.symbol);

    if (iconDefinition) {
        image = new Icon({
            src: iconDefinition.src,
            color: annotationStyle.color,
            scale: annotationStyle.size / 12
        });
    } else if (annotationStyle.symbol === 'circle') {
        image = new CircleStyle({
            radius: annotationStyle.size,
            fill: fill,
            stroke: stroke
        });
    } else {
        var shapeOptions = {
            fill: fill,
            stroke: stroke,
            radius: annotationStyle.size,
            points: 4,
            angle: 0
        };

        if (annotationStyle.symbol === 'square') {
            shapeOptions.angle = Math.PI / 4;
        } else if (annotationStyle.symbol === 'triangle') {
            shapeOptions.points = 3;
        } else if (annotationStyle.symbol === 'star') {
            shapeOptions.points = 5;
            shapeOptions.radius2 = Math.max(3, Math.round(annotationStyle.size * 0.48));
        }

        image = new RegularShape(shapeOptions);
    }

    if (labelText) {
        text = new Text({
            text: wrapAnnotationLabel(labelText),
            font: '600 13px "Public Sans", sans-serif',
            textAlign: 'center',
            overflow: true,
            // QA-213: anclada por su borde inferior el bloque solo crece hacia
            // arriba — con el ancla por defecto (centro vertical), un texto de
            // ~100 caracteres partido en varias líneas bajaba media caja y
            // tapaba el símbolo.
            textBaseline: 'bottom',
            offsetY: -(annotationStyle.size + 8),
            padding: [4, 6, 4, 6],
            fill: new Fill({ color: '#0e2a40' }),
            stroke: new Stroke({ color: '#ffffff', width: 3 }),
            backgroundFill: new Fill({ color: 'rgba(255, 255, 255, 0.88)' }),
            backgroundStroke: new Stroke({ color: 'rgba(14, 42, 64, 0.2)', width: 1 })
        });
    }

    annotationStyleCache[cacheKey] = new Style({ image: image, text: text });
    return annotationStyleCache[cacheKey];
}


export const annotations = {
    data() {
        return {
            message: "holi",
            featuresByUser: [],
            featuresByGroup: [],
            boundShowAnnotationModal: null,
            boundChangeCursor: null,
            boundSendAnnotationInfo: null
        }
    },
    methods: {
        setAnnotationMapCursor: function (cursor) {
            var target = this.map && this.map.getTargetElement
                ? this.map.getTargetElement()
                : null;
            if (target) target.style.cursor = cursor;
        },
        isAnnotationLayer: function (layer) {
            return layer === vectorAnnotationsByUser || layer === vectorAnnotationsByGroup;
        },
        addAnnotationInteraction: function () {
            // El @mousedown.capture del panel no dispara con Enter/Espacio en el botón.
            this.claimMapInteraction('annotations');
            this.removeAnnotationSelectorListener();
            this.setAnnotationMapCursor('crosshair');

            if (!this.boundShowAnnotationModal) {
                this.boundShowAnnotationModal = this.showAnnotationModal.bind(this);
            }
            this.map.un('singleclick', this.boundShowAnnotationModal);
            this.map.on('singleclick', this.boundShowAnnotationModal);
        },
        showAnnotationModal: function (evt) {
            var coordinates = proj.transform(evt.coordinate, 'EPSG:3857', 'EPSG:4326');
            if (this.openAnnotationModal) {
                this.openAnnotationModal(coordinates);
                return;
            }
            this.executeMapToolAction('annotations', 'showModal', { annotationCoor: coordinates });
        },
        removeAnnotationInteraction: function () {
            this.setAnnotationMapCursor('');
            if (this.boundShowAnnotationModal) {
                this.map.un('singleclick', this.boundShowAnnotationModal);
            }
            this.addAnnotationSelectorListener();
        },
        removeAnnotationSelectorListener: function () {
            if (this.boundChangeCursor) this.map.un('pointermove', this.boundChangeCursor);
            if (this.boundSendAnnotationInfo) this.map.un('singleclick', this.boundSendAnnotationInfo);
        },
        addAnnotationSelectorListener: function () {
            if (!this.boundSendAnnotationInfo) this.boundSendAnnotationInfo = this.sendAnnotationInfo.bind(this);
            if (!this.boundChangeCursor) this.boundChangeCursor = this.changeCursor.bind(this);

            this.map.un('singleclick', this.boundSendAnnotationInfo);
            this.map.un('pointermove', this.boundChangeCursor);
            this.map.on('singleclick', this.boundSendAnnotationInfo);
            this.map.on('pointermove', this.boundChangeCursor);
        },
        changeCursor: function (evt) {
            var feature = this.map.forEachFeatureAtPixel(evt.pixel,
                function (feature) {
                    return feature;
                }, {
                    hitTolerance: ANNOTATION_HIT_TOLERANCE_PX,
                    layerFilter: this.isAnnotationLayer
                });
            this.setAnnotationMapCursor(feature ? 'pointer' : '');
        },
        sendAnnotationInfo: function (evt) {
            var feature = this.map.forEachFeatureAtPixel(evt.pixel,
                function (feature) {
                    return feature;
                }, {
                    hitTolerance: ANNOTATION_HIT_TOLERANCE_PX,
                    layerFilter: this.isAnnotationLayer
                });

            var annotationInfo = {
                id: "",
                text: "",
                user: "",
                author: "",
                shared: "",
                map: "",
                groups: [],
                coordinates: [],
                style: null
            }
            if (feature) {
                this.setAnnotationMapCursor('');
                annotationInfo.id = feature.get('id')
                annotationInfo.text = feature.get('text')
                annotationInfo.shared = feature.get('shared')
                annotationInfo.map = feature.get('map')
                annotationInfo.groups = feature.get('groups')
                annotationInfo.coordinates = feature.get('coordinates')
                annotationInfo.style = feature.get('annotationStyle')
                if (feature.get('user')) {
                    annotationInfo.user = feature.get('user')
                    annotationInfo.author = feature.get('author') || ''
                }
                if (this.openAnnotationInfo) this.openAnnotationInfo(annotationInfo)
                else this.executeMapToolAction('annotations', 'showAnnotationInfo', { info: annotationInfo })
            }
        },
        getFeaturesByUser: function (array) {
            var annotationsByUser = deduplicateAnnotations(array);
            this.featuresByUser = new Array(annotationsByUser.length);
            for (var i = 0; i < annotationsByUser.length; ++i) {
                var annotationStyle = normalizeAnnotationStyle(
                    annotationsByUser[i].style,
                    DEFAULT_USER_ANNOTATION_STYLE
                );
                this.featuresByUser[i] = new Feature({
                    'geometry': new Point(proj.fromLonLat(annotationsByUser[i].location.coordinates)),
                    'id': annotationsByUser[i].id,
                    'text': annotationsByUser[i].text,
                    'map': annotationsByUser[i].id_map,
                    'groups': annotationsByUser[i].id_groups || [],
                    'coordinates': annotationsByUser[i].location.coordinates,
                    'shared': annotationsByUser[i].is_shared,
                    'annotationStyle': annotationStyle
                });
            }
        },
        createAnnatotionsByUserLayer: function (features) {
            this.removeAnnotationsByUserLayer();
            vectorAnnotationsByUser = new VectorLayer({
                source: new VectorSource({
                    features: features,
                }),
                style: function (feature) {
                    return getAnnotationStyle(feature, DEFAULT_USER_ANNOTATION_STYLE);
                }
            });
            vectorAnnotationsByUser.set('annotationLayer', true);
            this.map.addLayer(vectorAnnotationsByUser);

        },
        removeAnnotationsByUserLayer: function () {
            if (this.map && vectorAnnotationsByUser) this.map.removeLayer(vectorAnnotationsByUser)
            vectorAnnotationsByUser = null
        },
        getFeaturesByGroup: function (array, user) {
            var annotationsByGroup = deduplicateAnnotations(array);
            this.featuresByGroup = [];
            for (var i = 0; i < annotationsByGroup.length; ++i) {
                var isOwnAnnotation = annotationsByGroup[i].id_user == user;
                if (!isOwnAnnotation || !this.showUserAnnotations) {
                    var annotationStyle = normalizeAnnotationStyle(
                        annotationsByGroup[i].style,
                        DEFAULT_GROUP_ANNOTATION_STYLE
                    );
                    this.featuresByGroup.push(new Feature({
                        'geometry': new Point(proj.fromLonLat(annotationsByGroup[i].location.coordinates)),
                        'id': annotationsByGroup[i].id,
                        'text': annotationsByGroup[i].text,
                        'map': annotationsByGroup[i].id_map,
                        'groups': annotationsByGroup[i].id_groups || [],
                        'coordinates': annotationsByGroup[i].location.coordinates,
                        'user': isOwnAnnotation ? '' : annotationsByGroup[i].id_user,
                        'author': annotationsByGroup[i].author || '',
                        'shared': annotationsByGroup[i].is_shared,
                        'annotationStyle': annotationStyle
                    }))
                }
            }
        },
        createAnnotationsByGroupLayer: function (features) {
            this.removeAnnotationsByGroupLayer();
            vectorAnnotationsByGroup = new VectorLayer({
                source: new VectorSource({
                    features: features,
                }),
                style: function (feature) {
                    return getAnnotationStyle(feature, DEFAULT_GROUP_ANNOTATION_STYLE);
                }
            });
            vectorAnnotationsByGroup.set('annotationLayer', true);
            this.map.addLayer(vectorAnnotationsByGroup)
        },
        removeAnnotationsByGroupLayer: function () {
            if (this.map && vectorAnnotationsByGroup) this.map.removeLayer(vectorAnnotationsByGroup)
            vectorAnnotationsByGroup = null
        }
    },
    watch: {
        'featuresByUser': function (value) {
            if (this.showUserAnnotations && this.isActive) {
                this.createAnnatotionsByUserLayer(value)
            }
        },
        'featuresByGroup': function (value) {
            if (!this.showGroupAnnotations || !this.isActive) return;
            this.createAnnotationsByGroupLayer(value)
        }
    },
}
